import React from 'react';
import { ArrowRightIcon } from 'lucide-react';
import { usePortal } from '../../contexts/PortalContext';
import { DeliverableGroups } from '../../components/DeliverableGroups';
import { clientHeadline, projectStatus } from '../../utils/status';
import { formatShootDate, greeting, isUpcoming } from '../../utils/time';
export function Home() {
  const {
    activeProject,
    clientDeliverables,
    goClient,
    openDeliverable,
    clientAlerts,
    dismissAlert,
    projectComplete,
    clientName
  } = usePortal();
  const headline = clientHeadline(activeProject, clientDeliverables, projectComplete);
  const upcomingShoot = [...activeProject.shoots].filter(entry => isUpcoming(entry.date)).sort((a, b) => a.date.localeCompare(b.date))[0];
  const nextShootDate = upcomingShoot?.date ?? (isUpcoming(activeProject.nextShootDate) ? activeProject.nextShootDate : '');
  const nextShootLabel = nextShootDate ? formatShootDate(nextShootDate) : 'To be confirmed';
  const nextShootTime = upcomingShoot?.time || activeProject.nextShootTime;
  const nextShootLocation = upcomingShoot?.location || activeProject.location;
  return <div data-id="element-586" data-mp="2fs22twl5wo1b">
      <section data-id="element-587" data-mp="2fp63q4xkvtba">
        <p className="label text-mute" data-id="element-588" data-mp="2as7dhxh1c6id">
          {greeting()}
          {clientName ? ` ${clientName}` : ''}
        </p>
        <h1 className="mt-6 font-display text-6xl leading-[0.9] tracking-tight sm:text-8xl" data-id="element-589" data-mp="25tmd9y1s9mtx">
          {activeProject.client.toUpperCase()}
        </h1>
        <p className="mt-8 font-display text-2xl text-charcoal sm:text-3xl" data-id="element-590" data-mp="20vb4zrr4zkdt">
          {activeProject.name.toUpperCase()}
        </p>
        <p className="label mt-5 text-mute" data-id="element-591" data-mp="1s1g631glyq6m">
          {activeProject.type} · {projectStatus(activeProject)}
        </p>
      </section>

      {clientAlerts.length > 0 && <ul className="m-0 mt-14 list-none p-0 sm:mt-20" data-id="element-592" data-mp="1n0s63qaeiz09">
          {clientAlerts.map(alert => <li key={alert.id} className="border border-ink px-6 py-6 sm:px-8 [&+li]:mt-4" data-id="element-593" data-mp="1i5cvr8blrl8l">
              <div className="flex flex-wrap items-start justify-between gap-6" data-id="element-594" data-mp="1d6ic645t9snf">
                <div className="min-w-0" data-id="element-595" data-mp="13fi75r8q266h">
                  <p className="label m-0 text-ink" data-id="element-596" data-mp="18fmr5r8u9j1g">{alert.title}</p>
                  <p className="mt-3 font-display text-xl leading-snug text-charcoal" data-id="element-597" data-mp="18dtik6eo0oqt">
                    {alert.body}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-6" data-id="element-598" data-mp="yirvdg3y9wqv">
                  <button type="button" onClick={() => {
              if (alert.kind === 'review') openDeliverable(alert.deliverableId);else goClient('files');
              dismissAlert(alert.id);
            }} className="label border border-ink px-6 py-3.5 transition-colors duration-150 ease-out hover:bg-ink hover:text-paper" data-id="element-599" data-mp="1m7ku1kbyiip6">
                    {alert.actionLabel}
                  </button>
                  <button type="button" onClick={() => dismissAlert(alert.id)} aria-label="Dismiss notification" className="label text-mute transition-colors duration-150 ease-out hover:text-ink" data-id="element-600" data-mp="29xx8le1pjdoq">
                    Dismiss
                  </button>
                </div>
              </div>
            </li>)}
        </ul>}

      <section aria-labelledby="next-action" className="mt-14 bg-ink px-7 py-12 text-paper sm:mt-20 sm:px-12 sm:py-16" data-id="element-601" data-mp="3apvi5xlciel">
        <p id="next-action" className="label text-paper/50" data-id="element-602" data-mp="36pv84yilhik0">
          {headline.heading}
        </p>

        <p className="mt-7 max-w-3xl font-display text-3xl leading-tight sm:text-5xl" data-id="element-603" data-mp="3lmw45e7tjzoa">
          {headline.message}
        </p>
        <p className="mt-6 max-w-2xl font-display text-lg text-paper/60 sm:text-xl" data-id="element-604" data-mp="3gmq4bareb6v4">
          {headline.support}
        </p>

        <div className="mt-10 flex flex-wrap items-center gap-8" data-id="element-605" data-mp="10yhsozwnfjm6">
          {headline.actionId ? <button type="button" onClick={() => openDeliverable(headline.actionId as string)} className="label group flex items-center gap-3 border border-paper px-7 py-4 transition-colors duration-150 ease-out hover:bg-paper hover:text-ink" data-id="element-606" data-mp="vzn0x8gpekpx">
              {headline.actionLabel}
              <ArrowRightIcon className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover:translate-x-1" strokeWidth={1.25} data-id="element-607" data-mp="3flvkgnz7nx2z" />
            </button> : projectComplete ? <button type="button" onClick={() => goClient('files')} className="label group flex items-center gap-3 border border-paper px-7 py-4 transition-colors duration-150 ease-out hover:bg-paper hover:text-ink" data-id="element-608" data-mp="3php40rbckqu9">
              View Files
              <ArrowRightIcon className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover:translate-x-1" strokeWidth={1.25} data-id="element-609" data-mp="77dh2zk7t5pp" />
            </button> : null}

          {nextShootDate && !projectComplete && <p className="label m-0 text-paper/40" data-id="element-610" data-mp="h8pkk8a2skrr">Your next shoot is {nextShootLabel}</p>}
        </div>
      </section>

      <div className="mt-16 grid grid-cols-1 gap-14 sm:mt-24 lg:grid-cols-12 lg:gap-16" data-id="element-611" data-mp="ccgy09tjtpak">
        <section aria-labelledby="deliverables-heading" className="lg:col-span-8" data-id="element-612" data-mp="8alwb47ph9w9">
          <h2 id="deliverables-heading" className="label border-b border-rule pb-4 text-graphite" data-id="element-613" data-mp="14yjuagz3lt7s">
            Deliverables
          </h2>
          <div className="mt-8" data-id="element-614" data-mp="v98ob37id4o4">
            <DeliverableGroups data-id="element-615" data-mp="v8gr5ny9iyvq" />
          </div>
        </section>

        <aside className="lg:col-span-4 lg:border-l lg:border-rule lg:pl-12" data-id="element-616" data-mp="r7n5704dru5h">
          <h2 className="label border-b border-rule pb-4 text-graphite" data-id="element-617" data-mp="r5khimqpad3x">Next Shoot</h2>
          <p className="mt-8 font-display text-3xl leading-tight" data-id="element-618" data-mp="1nquh71dcxgk9">{nextShootLabel}</p>
          {nextShootDate && (nextShootTime || nextShootLocation) && <p className="mt-3 font-display text-xl text-charcoal" data-id="element-619" data-mp="3d6ex6uokmkjt">
              {[nextShootTime, nextShootLocation].filter(Boolean).join(' · ')}
            </p>}
          {nextShootDate && upcomingShoot?.title && <p className="label mt-6 text-mute" data-id="element-620" data-mp="33gc7b3cqxmpp">{upcomingShoot.title}</p>}
          {nextShootDate && <button type="button" onClick={() => goClient('schedule')} className="label mt-8 border border-ink px-6 py-3.5 transition-colors duration-150 ease-out hover:bg-ink hover:text-paper" data-id="element-621" data-mp="38e58fhz6zqj4">
              View Details
            </button>}
        </aside>
      </div>
    </div>;
}